import React from 'react';
import { Box, Grid, Typography } from '@material-ui/core';
import i18n from 'i18n-js';
import { Field } from 'formik';
import { useStyles } from '../ReservationRulesetCreate.styled';
import { TextField } from 'components/FormFields';
import { RuleField } from './RuleField';
import { useField } from 'formik';
import { accessControl } from '../../../components/AccessControl';

const RulesetInputs = ({ rule, errors, editable = true }) => {
  const [concurrentField] = useField(`${rule}.concurrent_max`);
  const multipleBufferEnabled = accessControl([
    'configuration.reservation.multiple_buffer.enabled.true',
  ]);
  const classes = useStyles();
  return (
    <>
      <Typography variant="h6" align="left" className={classes.rulesTitle}>
        {i18n.t('reservation_rulesets.reservation_times')}
      </Typography>
      <Grid container justifyContent="space-between" spacing={10}>
        <Grid item xs={12} md={6}>
          <RuleField
            title={i18n.t('reservation_rulesets.duration_min')}
            description={i18n.t('reservation_rulesets.duration_min_description')}
            name={`${rule}.duration_min`}
            editable={editable}
          />
          <RuleField
            title={i18n.t('reservation_rulesets.duration_max')}
            description={i18n.t('reservation_rulesets.duration_max_description')}
            name={`${rule}.duration_max`}
            errors={errors}
            editable={editable}
          />
          <RuleField
            title={i18n.t('reservation_rulesets.timedelta_early_start')}
            description={i18n.t('reservation_rulesets.timedelta_early_start_description')}
            name={`${rule}.timedelta_early_start`}
            editable={editable}
          />
          <RuleField
            title={i18n.t('reservation_rulesets.timedelta_early_end')}
            description={i18n.t('reservation_rulesets.timedelta_early_end_description')}
            name={`${rule}.timedelta_early_end`}
            editable={editable}
          />
          <RuleField
            title={i18n.t('reservation_rulesets.timedelta_end_margin_buffer')}
            description={i18n.t('reservation_rulesets.timedelta_end_margin_buffer_description')}
            name={`${rule}.timedelta_end_margin_buffer`}
            editable={editable}
          />
        </Grid>
        <Grid item xs={12} md={6}>
          {rule !== 'driver_rules' || !multipleBufferEnabled ? (
            <RuleField
              title={i18n.t('reservation_rulesets.timedelta_end_service_buffer')}
              description={i18n.t('reservation_rulesets.timedelta_end_service_buffer_description')}
              name={`${rule}.timedelta_end_service_buffer`}
              editable={editable}
            />
          ) : null}
          <RuleField
            title={i18n.t('reservation_rulesets.timedelta_reserve_ahead_min')}
            description={i18n.t('reservation_rulesets.timedelta_reserve_ahead_min_description')}
            name={`${rule}.timedelta_reserve_ahead_min`}
            editable={editable}
          />
          <RuleField
            title={i18n.t('reservation_rulesets.timedelta_reserve_ahead_max')}
            description={i18n.t('reservation_rulesets.timedelta_reserve_ahead_max_description')}
            name={`${rule}.timedelta_reserve_ahead_max`}
            errors={errors}
            editable={editable}
          />
          <RuleField
            title={i18n.t('reservation_rulesets.timedelta_vehicle_confirmed')}
            description={i18n.t('reservation_rulesets.timedelta_vehicle_confirmed_description')}
            name={`${rule}.timedelta_vehicle_confirmed`}
            editable={editable}
          />
        </Grid>
      </Grid>
      <Typography variant="h6" align="left" className={classes.rulesTitle}>
        {i18n.t('reservation_rulesets.cancel_reservation_rules')}
      </Typography>
      <Grid container justifyContent="space-between" spacing={10}>
        <Grid item xs={12} md={6}>
          <RuleField
            title={i18n.t('reservation_rulesets.duration_min_cancelled')}
            description={i18n.t('reservation_rulesets.duration_min_cancelled_description')}
            name={`${rule}.duration_min_cancelled`}
            editable={editable}
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <RuleField
            title={i18n.t('reservation_rulesets.timedelta_times_locked')}
            description={i18n.t('reservation_rulesets.timedelta_times_locked_description')}
            name={`${rule}.timedelta_times_locked`}
            editable={editable}
          />
        </Grid>
      </Grid>
      <Typography variant="h6" align="left" className={classes.rulesTitle}>
        {i18n.t('reservation_rulesets.other')}
      </Typography>
      <Grid container justifyContent="space-between" spacing={10}>
        <Grid item xs={12} md={6}>
          <Grid container className={classes.ruleContainer} spacing={2}>
            <Grid item xs={12} sm={6}>
              <Typography variant="h6" align="right">
                <Box fontWeight="bold" fontSize={14}>
                  {i18n.t('reservation_rulesets.concurrent_max')}
                </Box>
              </Typography>
              <Typography variant="body1" align="right" className={classes.description}>
                {i18n.t('reservation_rulesets.concurrent_max_description')}
              </Typography>
            </Grid>
            <Grid item xs={6} sm={3}>
              {editable ? (
                <Field
                  data-cy="concurrent-max-value"
                  margin="none"
                  label={i18n.t('reservation_rulesets.value')}
                  name={`${rule}.concurrent_max`}
                  component={TextField}
                  inputProps={{ className: classes.input }}
                  editable={editable}
                />
              ) : (
                <b>{`${concurrentField.value}`}</b>
              )}
            </Grid>
          </Grid>
          <RuleField
            title={i18n.t('reservation_rulesets.timedelta_create_grace_period')}
            description={i18n.t('reservation_rulesets.timedelta_create_grace_period_description')}
            name={`${rule}.timedelta_create_grace_period`}
            editable={editable}
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <RuleField
            title={i18n.t('reservation_rulesets.timedelta_max_late')}
            description={i18n.t('reservation_rulesets.timedelta_max_late_description')}
            name={`${rule}.timedelta_max_late`}
            errors={errors}
            editable={editable}
          />
        </Grid>
      </Grid>
      <Typography variant="h6" align="left" className={classes.rulesTitle}>
        {i18n.t('reservation_rulesets.app_reservation_configuration')}
      </Typography>
      <Grid container justifyContent="space-between" spacing={10}>
        <Grid item xs={12} md={6}>
          <RuleField
            title={i18n.t('reservation_rulesets.start_time_resolution')}
            description={i18n.t('reservation_rulesets.start_time_resolution_description')}
            name={`${rule}.start_time_resolution`}
            editable={editable}
          />
          <RuleField
            title={i18n.t('reservation_rulesets.end_time_resolution')}
            description={i18n.t('reservation_rulesets.end_time_resolution_description')}
            name={`${rule}.end_time_resolution`}
            editable={editable}
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <RuleField
            title={i18n.t('reservation_rulesets.duration_default')}
            description={i18n.t('reservation_rulesets.duration_default_description')}
            name={`${rule}.duration_default`}
            editable={editable}
          />
          <RuleField
            title={i18n.t('reservation_rulesets.duration_adjust_end')}
            description={i18n.t('reservation_rulesets.duration_adjust_end_description')}
            name={`${rule}.duration_adjust_end`}
            editable={editable}
          />
        </Grid>
      </Grid>
    </>
  );
};

export { RulesetInputs };
